import React, { useState, useEffect } from 'react';
import { Layout } from './components/layout/Layout';
import { ToastProvider } from './components/common/Toast';
import { DataProvider } from './context/DataContext';
import { Dashboard } from './pages/Dashboard';
import { DistributeMusic } from './pages/DistributeMusic';
import { LiveSongs } from './pages/LiveSongs';
import { Artists } from './pages/Artists';
import { DistributionStores } from './pages/DistributionStores';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { useLocalStorage } from './hooks/useLocalStorage';

function App() {
  const [activeTab, setActiveTab] = useLocalStorage('naad-active-tab', 'dashboard');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate app initialization
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getPageTitle = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        return 'Dashboard';
      case 'distribute':
        return 'Distribute Music';
      case 'songs':
        return 'Live Songs';
      case 'artists':
        return 'Artists Management';
      case 'analytics':
        return 'Analytics';
      case 'stores':
        return 'Distribution Stores';
      case 'settings':
        return 'Settings';
      default:
        return 'Dashboard';
    }
  };

  const getPageSubtitle = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        return 'Overview of your music distribution activity';
      case 'distribute':
        return 'Upload and distribute your music to 478+ platforms';
      case 'songs':
        return 'Manage your distributed songs and track their status';
      case 'artists':
        return 'Manage artist profiles and information';
      case 'analytics':
        return 'Track performance metrics and revenue insights';
      case 'stores':
        return 'Browse and select from 478+ distribution platforms';
      case 'settings':
        return 'Configure your account and preferences';
      default:
        return '';
    }
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onTabChange={setActiveTab} />;
      case 'distribute':
        return <DistributeMusic />;
      case 'songs':
        return <LiveSongs />;
      case 'artists':
        return <Artists />;
      case 'analytics':
        return <Analytics />;
      case 'stores':
        return <DistributionStores />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard onTabChange={setActiveTab} />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-white font-display mb-2">Naad Music Group</h1>
          <p className="text-gray-400">Loading your music dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <DataProvider>
      <ToastProvider>
        <Layout
          activeTab={activeTab}
          onTabChange={setActiveTab}
          title={getPageTitle(activeTab)}
          subtitle={getPageSubtitle(activeTab)}
        >
          {renderPage()}
        </Layout>
      </ToastProvider>
    </DataProvider>
  );
}

export default App;
